/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Server-sent events, read with `fetch`. The framing, and nothing else.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EXTRACTED FOR C27, AND IT IS A DUPLICATED PREDICATE THAT WOULD OTHERWISE HAVE BEEN
 * WRITTEN TWICE.** `live-provider.tsx` has read `/events/stream` since C21; C27's agent turn reads a
 * second stream on a second path. The reconnect policy, the backoff and the frame vocabulary are
 * different for the two — deliberately, they are different problems — but **the framing is
 * identical**, and it is the part with the trap in it.
 *
 * `hard-won.md`'s warning about a copied predicate applies exactly: *"the copy that drifts is the
 * one deciding what a UI offers while the server quietly disagrees."* A second hand-rolled buffer
 * would work on both developers' laptops and corrupt one of the two streams for whoever is on a
 * train.
 *
 * ⚠️ **Why not `EventSource`, for the third time in this repository:** it cannot send headers
 * (R-19 — the access token is in memory only, deliberately), and it cannot POST at all, which the
 * agent's turn requires. Both readers therefore use `fetch` and this file.
 */

/**
 * The blocks of an SSE body, as they arrive.
 *
 * ⚠️⚠️ **FRAMES ARE SPLIT ON A BLANK LINE, AND A CHUNK IS NOT A FRAME.** A network read can end
 * anywhere — mid-JSON, mid-word — so the tail is kept and completed by the next one. Parsing per
 * chunk works perfectly on a fast local connection and corrupts events the first time somebody is
 * on a slow one, which is the kind of defect that only ever reproduces in the field.
 *
 * ⚠️ **Comment lines are dropped here.** Both endpoints open with `: connected` and `/events/stream`
 * ticks `: ping`; they are keep-alive traffic with no payload, and every caller would otherwise have
 * to know to skip them.
 *
 * ⚠️ **`{ stream: true }` on the decoder is not optional.** A multi-byte character — every accented
 * Hungarian letter — can be split across two reads, and a decode without it produces a replacement
 * character. The reply *"Nincs csúszásban"* would arrive with a `` at the sixth character on a bad
 * connection.
 */
export async function* readSseBlocks(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<string, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) {
        if (block.trim() === '' || block.startsWith(':')) continue;
        yield block;
      }
    }
  } finally {
    /**
     * ⚠️ **Cancelled rather than merely dropped, so a consumer that stops reading closes the
     * socket.** For the agent that is what makes a closed tab stop costing money: the server's loop
     * notices the dead response, breaks, and aborts the model call. Without this the browser holds
     * the connection until the response completes on its own.
     *
     * ⚠️ In a `finally`, so it runs whether the generator finished, threw, or was abandoned by a
     * `break` in the caller's `for await`.
     */
    await reader.cancel().catch(() => undefined);
  }
}

/**
 * The `event:` name of one block, or `'message'` when it carries none.
 *
 * ⚠️ `'message'` is the SSE default and both of this product's endpoints name every frame anyway —
 * `live.controller.ts` explains why (a stream whose every line reads `event: message` is one nobody
 * can debug with `curl -N`).
 */
export function sseEvent(block: string): string {
  return /^event: ([\w-]+)$/m.exec(block)?.[1] ?? 'message';
}

/**
 * The `data:` payload of one block, parsed.
 *
 * ⚠️ **Returns `null` for a block with no data or unparseable JSON rather than throwing.** A single
 * malformed frame must not end a stream that is otherwise fine — and the caller validates the shape
 * with a shared schema afterwards regardless, because `JSON.parse` returns `any` and trusting it is
 * `hard-won.md` C19-1.
 *
 * ⚠️ **Single-line only, which is what both servers emit** (`JSON.stringify` cannot produce a raw
 * newline inside a string, so a payload cannot span lines). A multi-line `data:` is legal SSE and is
 * not supported; it would be silently truncated, so it is stated rather than half-handled.
 */
export function sseData(block: string): unknown {
  const raw = /^data: (.*)$/m.exec(block)?.[1];
  if (raw === undefined) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}
